import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useMe } from "@/hooks/use-auth";
import { useCreateRider, useUpdateRider } from "@/hooks/use-riders";

import {
  defaultRiderValues,
  riderSchema,
  type RiderFormValues,
} from "./rider-schema";

import type { Rider } from "@artisancode/api-types";

export function useRiderForm(
  open: boolean,
  editing: Rider | null,
  onSuccess: () => void,
) {
  const { mutate: create, isPending: isCreating } = useCreateRider();
  const { mutate: update, isPending: isUpdating } = useUpdateRider();
  const { data: me } = useMe();

  const form = useForm<RiderFormValues>({
    resolver: zodResolver(riderSchema),
    defaultValues: defaultRiderValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(
        editing
          ? { name: editing.name }
          : { ...defaultRiderValues, workspaceId: me?.workspaceId ?? "" },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, me?.workspaceId]);

  function onSubmit(values: RiderFormValues) {
    if (editing) {
      update(
        { id: editing.id, name: values.name },
        {
          onSuccess: () => {
            toast.success("Rider berhasil diperbarui.");
            onSuccess();
          },
        },
      );
    } else {
      create(
        { name: values.name, workspaceId: values.workspaceId || undefined },
        {
          onSuccess: () => {
            toast.success("Rider berhasil ditambahkan.");
            onSuccess();
          },
        },
      );
    }
  }

  return {
    form,
    isSubmitting: isCreating || isUpdating,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
